import React from "react";

import {
  HvButton,
  HvTag,
  HvTypography,
  HvBaseSwitchProps,
  ListValueProp,
} from "@hitachivantara/uikit-react-core";
import {
  HvTableColumnConfig,
  HvCellProps,
  HvRowInstance,
} from "@hitachivantara/uikit-react-lab";
import { DropDownXS, DropRightXS } from "@hitachivantara/uikit-react-icons";
import { setId } from "lib/utils/ids";
import { stringFallback, numberFallback } from "lib/utils/fallbacks";
import OverflowTooltip from "../../OverflowTooltip";
import LevelBadge from "../../LevelBadge";
import DateColumnCell from "./DateColumnCell";
import IconColumnCell from "./IconColumnCell";
import { IconData } from "../../../../lib/api/incidentType";
import SwitchColumnCell from "./SwitchColumnCell";
import ProgressColumnCell from "./ProgressColumnCell";
import DropdownColumnCell from "./DropdownColumnCell";

export function textColumn<
  D extends Record<string, unknown>,
  C extends HvTableColumnConfig<D>
>(col: C): HvTableColumnConfig<D> {
  return {
    Cell: ({ value }: HvCellProps<D, string>) => (
      <OverflowTooltip data={stringFallback(value)} />
    ),
    sortType: "alphanumeric",
    ...col,
  } as HvTableColumnConfig<D>;
}

export function numberColumn<
  D extends Record<string, unknown>,
  C extends HvTableColumnConfig<D>
>(col: C): HvTableColumnConfig<D> {
  return {
    Cell: ({ value }: HvCellProps<D, number>) => numberFallback(value),
    align: "right",
    sortType: "number",
    ...col,
  } as HvTableColumnConfig<D>;
}

export function dateColumn<
  D extends Record<string, unknown>,
  C extends HvTableColumnConfig<D>
>(col: C): HvTableColumnConfig<D> {
  return {
    Cell: ({ value }: HvCellProps<D, string>) => (
      <DateColumnCell date={value} />
    ),
    sortType: "string",
    sortDescFirst: true,
    ...col,
  } as HvTableColumnConfig<D>;
}

export function actionsColumn<
  D extends Record<string, unknown>,
  C extends HvTableColumnConfig<D>
>(col: C): HvTableColumnConfig<D> {
  return {
    variant: "actions",
    disableSortBy: true,
    ...col,
  } as HvTableColumnConfig<D>;
}

export function textWithIconColumn<
  D extends Record<string, unknown>,
  C extends HvTableColumnConfig<D>
>(col: C, iconDataKey: keyof D): HvTableColumnConfig<D> {
  return {
    Cell: ({ row, value }: HvCellProps<D, string>) => {
      const icon = row.original[iconDataKey] as IconData;
      return (
        <IconColumnCell icon={icon}>
          <OverflowTooltip data={stringFallback(value)} />
        </IconColumnCell>
      );
    },
    sortType: "alphanumeric",
    cellStyle: {
      position: "relative",
    },
    ...col,
  } as HvTableColumnConfig<D>;
}

export function iconColumn<
  D extends Record<string, unknown>,
  C extends HvTableColumnConfig<D>
>(
  col: C,
  iconDataKey: keyof D,
  valueDataKey: keyof D,
  top = 5
): HvTableColumnConfig<D> {
  return {
    Cell: ({ value }: HvCellProps<D>) => {
      if (!value) {
        return "—";
      }

      const icon = value[iconDataKey] as IconData;

      return (
        <IconColumnCell icon={icon} style={{ top }}>
          {valueDataKey != null && (
            <OverflowTooltip data={stringFallback(value[valueDataKey])} />
          )}
        </IconColumnCell>
      );
    },
    sortType: "alphanumeric",
    cellStyle: {
      position: "relative",
    },
    ...col,
  } as HvTableColumnConfig<D>;
}

export function expandColumn<D extends Record<string, unknown>>(
  col: HvTableColumnConfig<D>,
  expandRowButtonAriaLabel: string,
  collapseRowButtonAriaLabel: string,
  getCanRowExpand?: (row: HvRowInstance<D>) => boolean
): HvTableColumnConfig<D> {
  return {
    Cell: ({ row, value }: HvCellProps<D>) => {
      const { onClick } = row.getToggleRowExpandedProps();

      const hasContent = getCanRowExpand?.(row) ?? true;

      return (
        <>
          {hasContent && (
            <HvButton
              icon
              category="ghost"
              aria-label={
                row.isExpanded
                  ? collapseRowButtonAriaLabel
                  : expandRowButtonAriaLabel
              }
              aria-expanded={row.isExpanded}
              onClick={onClick}
              style={{ position: "absolute", left: 0, top: 4 }}
            >
              {row.isExpanded ? <DropDownXS /> : <DropRightXS />}
            </HvButton>
          )}

          <OverflowTooltip data={stringFallback(value)} />
        </>
      );
    },
    sortType: "alphanumeric",
    cellStyle: {
      position: "relative",
    },
    ...col,
  } as HvTableColumnConfig<D>;
}

export function badgeColumn<
  D extends Record<string, unknown>,
  C extends HvTableColumnConfig<D>
>(
  col: C,
  prefix: string,
  badgeTextDataKey: keyof D,
  colorDataKey: keyof D,
  valueDataKey: keyof D,
  fromRowData = false,
  top = 4
): HvTableColumnConfig<D> {
  return {
    Cell: ({ row, value }: HvCellProps<D>) => {
      if (!value) {
        return "—";
      }

      const {
        [colorDataKey]: color,
        [valueDataKey]: name,
        [badgeTextDataKey]: order,
      } = fromRowData ? row.original : value;

      return (
        <LevelBadge
          absolutePosition
          name={name}
          color={color}
          textIcon={prefix + order?.toString()}
          style={{ top }}
        />
      );
    },
    cellStyle: {
      position: "relative",
    },
    ...col,
  } as HvTableColumnConfig<D>;
}

export function tagColumn<
  D extends Record<string, unknown>,
  C extends HvTableColumnConfig<D>
>(
  col: C,
  valueDataKey: keyof D,
  colorDataKey: keyof D,
  textColorDataKey: keyof D,
  fromRowData = false
): HvTableColumnConfig<D> {
  return {
    Cell: ({ row, value }: HvCellProps<D>) => {
      if (!value) {
        return "—";
      }

      const {
        [valueDataKey]: name,
        [colorDataKey]: color,
        [textColorDataKey]: textColor,
      } = fromRowData ? row.original : value;

      return (
        <HvTag
          label={<HvTypography variant="normalText">{name}</HvTypography>}
          type="categorical"
          color={color}
          style={textColor != null ? { color: textColor } : {}}
          tabIndex={-1}
        />
      );
    },
    cellStyle: {
      paddingTop: 0,
      paddingBottom: 0,
    },
    ...col,
  } as HvTableColumnConfig<D>;
}

export function switchColumn<
  D extends Record<string, unknown>,
  C extends HvTableColumnConfig<D>
>(
  col: C,
  switchLabel: string,
  falseLabel?: string,
  trueLabel?: string,
  switchProps?: HvBaseSwitchProps
): HvTableColumnConfig<D> {
  return {
    Cell: ({ value, row }: HvCellProps<D, boolean>) => {
      return (
        <SwitchColumnCell
          checked={value}
          value={row.id}
          switchLabel={switchLabel}
          falseLabel={falseLabel}
          trueLabel={trueLabel}
          switchProps={switchProps}
        />
      );
    },
    cellStyle: {
      paddingTop: 0,
      paddingBottom: 0,
    },
    ...col,
  } as HvTableColumnConfig<D>;
}

export function dropdownColumn<
  D extends Record<string, unknown>,
  C extends HvTableColumnConfig<D>
>(
  col: C,
  id: string,
  placeholder: string,
  disabledPlaceholder: string,
  onChange?: (id: string, value: ListValueProp) => void
): HvTableColumnConfig<D> {
  return {
    Cell: ({ value, row, column }: HvCellProps<D, ListValueProp[]>) => {
      const dsbld = value.length < 1;
      return (
        <DropdownColumnCell
          value={value}
          placeholder={dsbld ? disabledPlaceholder : placeholder}
          onChange={(val: ListValueProp) => onChange?.(row.id, val)}
          disabled={dsbld}
          dropdownProps={{
            "aria-labelledby": setId(id, column.id),
          }}
        />
      );
    },
    cellStyle: {
      paddingTop: 0,
      paddingBottom: 0,
    },
    ...col,
  } as HvTableColumnConfig<D>;
}

export function progressColumn<
  D extends Record<string, unknown>,
  C extends HvTableColumnConfig<D>
>(
  col: C,
  getPartial?: (row: HvRowInstance<D>) => number,
  getTotal?: (row: HvRowInstance<D>) => number,
  color?: "primary" | "secondary"
): HvTableColumnConfig<D> {
  return {
    Cell: ({ row }: HvCellProps<D, boolean>) => {
      const partial = getPartial?.(row) as number;
      const total = getTotal?.(row) as number;

      if (total) {
        return (
          <ProgressColumnCell partial={partial} total={total} color={color} />
        );
      }

      return "—";
    },

    cellStyle: {
      paddingTop: 0,
      paddingBottom: 0,
    },
    ...col,
  } as HvTableColumnConfig<D>;
}
